import React from "react";

export type ProgressType = "bar" | "circle";

interface ProgressProps {
    /** Value from 0 to 100 */
    value: number;
    /** Visual style: 'bar' (default) or 'circle' */
    type?: ProgressType;
    /** Show the percentage label */
    showLabel?: boolean;
    /** Bar height in px (only for type='bar') */
    height?: number;
    /** Circle size in px (only for type='circle') */
    circleSize?: number;
    /** Show the label inside the bar (only for type='bar') */
    embedded?: boolean;
    /** Color class name, defaults to 'primary' */
    color?: string;
    /** Text color class name. Defaults to 'white' if embedded, 'black' otherwise. */
    textColor?: string;
    /** Track color class name (for the unfilled part). Defaults to the main color but faded. */
    trackColor?: string;
    className?: string;
}

export const Progress: React.FC<ProgressProps> = ({
    value,
    type = "bar",
    showLabel = true,
    height = 8,
    circleSize = 36,
    embedded = false,
    color = "primary",
    textColor,
    trackColor,
    className = "",
}) => {
    const resolvedTextColor = textColor || (embedded ? "white" : "black");

    const isGrayTrack = !trackColor && !embedded;
    const resolvedTrackColor = trackColor || (embedded ? color : "slate-200");

    const circleTrackClass = isGrayTrack
        ? `stroke-${resolvedTrackColor}`
        : `stroke-${resolvedTrackColor}/20`;
    const barTrackBgClass = isGrayTrack
        ? `bg-${resolvedTrackColor}/80 dark:bg-white/15`
        : `bg-${resolvedTrackColor}/20 dark:bg-white/10`;
    const barTrackRingClass = isGrayTrack
        ? `ring-1 ring-${resolvedTrackColor}/40 dark:ring-white/10`
        : "";

    const pct = Math.min(100, Math.max(0, Math.round(value)));

    if (type === "circle") {
        const radius = (circleSize - 4) / 2;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (pct / 100) * circumference;

        return (
            <span
                className={`relative inline-flex items-center justify-center ${className}`}
                style={{ width: circleSize, height: circleSize }}
            >
                <svg
                    width={circleSize}
                    height={circleSize}
                    className="-rotate-90"
                >
                    {/* Track */}
                    <circle
                        cx={circleSize / 2}
                        cy={circleSize / 2}
                        r={radius}
                        strokeWidth={3}
                        className={`${circleTrackClass} dark:stroke-white/15 fill-none`}
                    />
                    <circle
                        cx={circleSize / 2}
                        cy={circleSize / 2}
                        r={radius}
                        strokeWidth={3}
                        className={`fill-none transition-all duration-300 ${color === "primary" ? "stroke-primary" : `stroke-${color}`}`}
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                    />
                </svg>
                {showLabel && (
                    <span
                        className={`absolute text-[9px] font-bold text-${resolvedTextColor}`}
                    >
                        {pct}%
                    </span>
                )}
            </span>
        );
    }

    // Default: bar
    return (
        <div
            className={`flex items-center ${!embedded ? "space-x-2" : ""} min-w-[100px] ${className}`}
        >
            <div
                className={`flex-1 relative ${barTrackBgClass} rounded-full overflow-hidden ${barTrackRingClass}`}
                style={{ height: height }}
            >
                <div
                    className={`rounded-full transition-all duration-300 h-full ${color === "primary" ? "bg-primary" : `bg-${color}`}`}
                    style={{ width: `${pct}%` }}
                />
                {embedded && showLabel && (
                    <div
                        className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold text-${resolvedTextColor} pointer-events-none`}
                    >
                        {pct}%
                    </div>
                )}
            </div>
            {!embedded && showLabel && (
                <span
                    className={`text-[11px] font-bold text-${resolvedTextColor} w-8 text-right shrink-0`}
                >
                    {pct}%
                </span>
            )}
        </div>
    );
};

export default Progress;
